import React, {
  useContext,
  createContext,
  useEffect,
  useState,
  useMemo
} from 'react'
import { useLocation, useParams } from 'react-router-dom'

import { useCompletedOrderQuery } from 'src/apollo/onlineOrdering'

import { useRestaurant } from 'shared/components/common/restaurant_context/RestaurantContext'

import { CompletedOrder } from './types'

type CompletedOrderContextType = {
  orderGuid?: string | null
  loadingCompletedOrder: boolean
  completedOrder?: CompletedOrder | null
  completedOrderError: string | null
  locationGuidFromUrl?: string | null
}

type LocationState = {
  completedOrder: CompletedOrder | null
}

export const CompletedOrderContext = createContext<
  CompletedOrderContextType | undefined
>(undefined)
export const ORDER_KEY = 'toast-embedded-oo-order'

export const CompletedOrderContextProvider = (
  props: React.PropsWithChildren<{}>
) => {
  const location = useLocation<LocationState>()
  const { selectedLocation, updateLocation } = useRestaurant()
  const [loadingCompletedOrder, setLoadingCompletedOrder] = useState(true)
  const [orderGuid, setOrderGuid] = useState<string | null>(null)
  const [order, setOrder] = useState<CompletedOrder | null>(null)
  const [completedOrderError, setCompletedOrderError] = useState<string | null>(
    null
  )
  const { pathOrderGuid } = useParams<{ pathOrderGuid: string }>()

  // Extract locationGuid from URL params
  const locationGuidFromUrl = useMemo(() => {
    if (location.search) {
      const params = new URLSearchParams(location.search)
      return params.get('locationGuid')
    }
    return null
  }, [location.search])

  const rxGuid = locationGuidFromUrl || selectedLocation?.externalId

  useEffect(() => {
    if (location.state?.completedOrder) {
      setOrder(location.state.completedOrder as CompletedOrder)
      setLoadingCompletedOrder(false)
    } else if (pathOrderGuid) {
      setOrderGuid(pathOrderGuid)
    } else if (typeof window !== 'undefined') {
      const orderGuid = localStorage.getItem(ORDER_KEY)
      if (orderGuid) {
        setOrderGuid(orderGuid)
      } else {
        // No order found in any source, stop loading to show "order not found" UI
        setLoadingCompletedOrder(false)
      }
    }
  }, [location, pathOrderGuid])

  // Update location in context when URL param differs from current selection
  useEffect(() => {
    if (
      locationGuidFromUrl &&
      locationGuidFromUrl !== selectedLocation?.externalId
    ) {
      updateLocation(locationGuidFromUrl)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationGuidFromUrl])

  const { data, loading, error } = useCompletedOrderQuery({
    ssr: false,
    skip: !orderGuid || !rxGuid,
    variables: {
      input: {
        orderGuid: orderGuid || '',
        restaurantGuid: rxGuid || ''
      }
    },
    notifyOnNetworkStatusChange: true
  })

  useEffect(() => {
    if (data === undefined) return
    if (data?.completedOrder?.__typename === 'CompletedOrder') {
      setOrder(data.completedOrder as CompletedOrder)
    } else {
      setCompletedOrderError('Order not found')
    }
    setLoadingCompletedOrder(false)
  }, [data])

  useEffect(() => {
    if (error) {
      setCompletedOrderError(error.message || 'Unknown error')
      setLoadingCompletedOrder(false)
    }
  }, [error])

  return (
    <CompletedOrderContext.Provider
      value={{
        orderGuid,
        loadingCompletedOrder: loadingCompletedOrder || loading,
        completedOrder: order,
        completedOrderError,
        locationGuidFromUrl
      }}
    >
      {props.children}
    </CompletedOrderContext.Provider>
  )
}

export const useCompletedOrder = () => {
  const context = useContext(CompletedOrderContext)
  if (!context) {
    throw new Error(
      'useCompletedOrder must be used within a CompletedOrderContextProvider'
    )
  }

  return context
}
